import { Button } from '@/components/ui/button'
import { urlSafeProfilePath } from '@/helpers/url-safe-profile-path'
import { getUserProfilePictureUrl } from '@/helpers/user'
import { cn } from '@/lib/utils'
import Image from 'next/image'
import Link from 'next/link'

export default function CastingCard({ profile }) {
  const role = profile.role
  const isBroadcaster = role === 'Broadcaster'
  const isCommentator = role === 'Commentator'

  // Darker theme logic
  let borderColorClass = 'border-neutral-blue-700'
  let roleColorClass = 'text-neutral-blue-200'

  if (isBroadcaster) {
    borderColorClass = 'border-[#9d4edd]' // Purple
    roleColorClass = 'text-[#9d4edd]'
  } else if (isCommentator) {
    borderColorClass = 'border-[#ff9100]' // Orange
    roleColorClass = 'text-[#ff9100]'
  }

  const roleText = role

  const cleanBio = profile.description?.replace(/<[^>]+>/g, '') || ''
  const bioSummary =
    cleanBio.length > 100 ? cleanBio.substring(0, 100) + '...' : cleanBio
  const rawLanguages = [
    profile.user?.primaryLanguage,
    ...(profile.user?.otherLanguages || []),
  ].filter(Boolean)
  const languages = Array.from(new Set(rawLanguages))
  const visibleLanguages = languages.slice(0, 2)

  return (
    <div
      className={cn(
        'shadow-xs group flex flex-col rounded-lg border-2 bg-neutral-blue-800 p-5 transition-all hover:shadow-md',
        borderColorClass,
      )}
    >
      <div className="flex items-center gap-4">
        <div className="relative h-16 w-16 shrink-0 overflow-hidden rounded-full border border-neutral-blue-600 bg-neutral-blue-900">
          <Image
            src={getUserProfilePictureUrl(profile.user)}
            layout="fill"
            objectFit="cover"
            alt={`${profile.displayName} logo`}
          />
        </div>
        <div className="flex flex-col">
          <h3 className="line-clamp-1 text-lg font-bold text-shades-0">
            {profile.displayName}
          </h3>
          <span
            className={cn(
              'text-xs font-semibold uppercase tracking-wider',
              roleColorClass,
            )}
          >
            {roleText}
          </span>
        </div>
      </div>

      <div className="mt-4 flex flex-1 flex-col gap-2">
        <div className="flex flex-col gap-2 text-xs text-shades-0">
          <div className="flex flex-wrap gap-2">
            {visibleLanguages.map((lang) => (
              <span
                key={lang}
                className="rounded border border-neutral-blue-600 bg-neutral-blue-700 px-2 py-0.5"
              >
                {lang}
              </span>
            ))}
            {languages.length > visibleLanguages.length && (
              <span className="px-2 py-0.5 text-neutral-blue-200">
                +{languages.length - visibleLanguages.length}
              </span>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {profile.games?.slice(0, 3).map((g) => (
              <span
                key={g.name}
                className="rounded border border-neutral-blue-500 bg-neutral-blue-600 px-2 py-0.5"
              >
                {g.nicename}
              </span>
            ))}
            {profile.games?.length > 3 && (
              <span className="px-2 py-0.5 text-neutral-blue-200">
                +{profile.games.length - 3}
              </span>
            )}
          </div>
        </div>

        <p className="mt-2 line-clamp-3 text-sm text-neutral-blue-200">
          {bioSummary || 'No bio available.'}
        </p>
      </div>

      <Link
        href={urlSafeProfilePath({
          ...profile.user,
          id: profile.userId,
          profileType: isBroadcaster ? 'broadcaster' : 'commentator',
        })}
        className="mt-5 w-full"
      >
        <Button className="w-full border border-neutral-blue-600 bg-neutral-blue-700 text-shades-0 transition-all duration-300 hover:bg-shades-0 hover:text-neutral-blue-900">
          View Profile
        </Button>
      </Link>
    </div>
  )
}
